import { Box, Flex, Stack } from '@web/components/atoms/Layout'
import { ComponentType, type DollarHandles as DollarHandlesProps } from '@web/graphql-components'
import { EMPTY_TEXT } from '@web/components/page-components/default'
import { useEffect, useMemo } from 'react'
import { v4 as uuid } from 'uuid'
import { useMergeFields } from '@web/components/page-components/Text/MergeTags/MergeTag'
import { useColorWithOpacity } from '@web/components/molecules/ColorPicker/utils'
import { formatAmount } from '@web/components/utils/format'
import { ComponentWrapper } from '../utils'
import { useMediaQuery } from '@web/components/utils/chakra-hooks'
import {
  descriptionAreaMinHeight,
  descriptionJustify,
  getDollarHandleMetrics,
  useEqualMinHeight,
} from './dollarHandleLayout'

const mouseClickEvents = ['mousedown', 'click', 'mouseup']
function simulateMouseClick(element: HTMLElement) {
  mouseClickEvents.forEach((mouseEventType) =>
    element.dispatchEvent(
      new MouseEvent(mouseEventType, {
        view: window,
        bubbles: true,
        cancelable: true,
        buttons: 1,
      }),
    ),
  )
}

type UseComponentStylesProps = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  styles: any
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  mobileStyles: any
}

const useComponentStyles = (props: UseComponentStylesProps) => {
  const [isMobile] = useMediaQuery(['(max-width: 900px)'], { fallback: [] })
  const styles = useMemo(() => {
    if (isMobile) {
      const { padding, margin, ...rest } = props?.styles ?? {}

      return {
        ...rest,
        ...props.mobileStyles,
      }
    }

    return props.styles
  }, [props.styles, props.mobileStyles, isMobile])

  return styles
}

const DollarHandles = (
  props: DollarHandlesProps &
    ComponentWrapper & {
      onSelect: (amount: number) => void
    },
) => {
  const [isMobile] = useMediaQuery(['(max-width: 900px)'], { fallback: [] })
  const { id, dollarHandles } = props
  const dollarHandleIds = useMemo(() => dollarHandles.map(() => uuid()), [dollarHandles.length])
  const Component = props.Component

  const styles = useComponentStyles({ styles: props.styles, mobileStyles: props.mobileStyles })
  const metrics = getDollarHandleMetrics(styles)
  const isHorizontal = metrics.isHorizontal
  const descriptionKey = dollarHandles.map((handle) => handle.description ?? '').join('\u0000')
  const { minHeight: descriptionContentHeight, setRef: setDescriptionRef } = useEqualMinHeight(
    dollarHandles.length,
    descriptionKey,
  )
  const descriptionMinHeight = metrics.equalizeDescriptions
    ? descriptionAreaMinHeight(descriptionContentHeight, metrics.resolvedDescriptionPadding)
    : undefined

  const backgroundColor = useColorWithOpacity(styles?.backgroundColor ?? '#FFFFFF', styles?.backgroundColorOpacity)
  const borderColor = useColorWithOpacity(styles?.borderColor ?? '#E2E8F0', styles?.borderColorOpacity)

  useEffect(() => {
    const setMergeField = useMergeFields.getState().setMergeField
    dollarHandles.forEach((handle, i) => {
      setMergeField(`DollarHandles.Ask${i + 1}`, `${formatAmount(handle.amount)}`)
    })
  }, [])

  return (
    <Flex
      justifyContent="center"
      w="100%"
      css={{
        '@container page (min-width: 0px)': {
          p: props.mobileStyles?.padding ?? 0,
        },
        '@container page (min-width: 901px)': {
          p: props.styles?.padding ?? 0,
        },
      }}
    >
      <Stack
        w="100%"
        {...(metrics.spaceBetween !== undefined ? { gap: `${metrics.spaceBetween}px` } : {})}
        css={{
          '@container page (min-width: 0px)': {
            flexDirection: 'column',
          },
          '@container page (min-width: 901px)': {
            flexDirection: isHorizontal ? 'row' : 'column',
            alignItems: isHorizontal ? 'stretch' : undefined,
          },
        }}
      >
        {dollarHandles.map((dollarHandle, index) => {
          const { amount, description, imageUrl } = dollarHandle
          let { hasImage, hasDescription } = dollarHandle
          if (hasImage === undefined || hasImage === null) {
            hasImage = imageUrl !== undefined
          }
          if (hasDescription === undefined || hasDescription === null) {
            hasDescription = description !== undefined
          }

          return (
            <Stack
              cursor="pointer"
              key={dollarHandleIds[index]}
              direction={isHorizontal ? 'column' : 'row'}
              {...(isHorizontal && !metrics.descriptionPadding ? { paddingBottom: '24px' } : {})}
              minH="100px"
              minW="100px"
              w="100%"
              h={metrics.equalizeDescriptions ? '100%' : undefined}
              alignItems={metrics.equalizeDescriptions ? 'stretch' : 'start'}
              borderRadius={`${styles?.borderRadius ?? 4}px`}
              overflow="hidden"
              transition="all 0.2s ease-in-out"
              css={{
                border: '2px solid',
                backgroundColor,
                borderColor,
              }}
              _hover={{
                boxShadow: '0px 6px 16px -8px #0D121640, 0px 10px 27px -5px #0D121626',
              }}
              _active={{
                boxShadow: '0px 6px 16px -8px #0D121640',
              }}
              onClick={() => {
                let element: HTMLElement | null = document.querySelector(
                  `#${isMobile ? 'mobile' : 'desktop'}-layout #dollar-handle-${amount}`,
                )

                if (element) {
                  setTimeout(() => {
                    element?.scrollIntoView({
                      behavior: 'smooth',
                      block: 'center',
                      inline: 'center',
                    })
                  }, 0)

                  simulateMouseClick(element)
                } else {
                  if (!element) {
                    element = document.querySelector(`#${isMobile ? 'mobile' : 'desktop'}-layout #dollar-handle-custom`)
                    props.onSelect(amount)
                  }
                  setTimeout(() => {
                    element?.scrollIntoView({
                      behavior: 'smooth',
                      block: 'center',
                      inline: 'center',
                    })
                  }, 0)
                }
              }}
            >
              {hasImage &&
                imageUrl &&
                (metrics.imageHeight !== undefined ? (
                  <Flex
                    w={isHorizontal ? '100%' : `${metrics.imageWidth}px`}
                    minW={isHorizontal ? undefined : `${metrics.imageWidth}px`}
                    h={`${metrics.imageHeight}px`}
                    flexShrink={0}
                    alignSelf={isHorizontal ? 'stretch' : 'flex-start'}
                    position="relative"
                    overflow="hidden"
                  >
                    <Component
                      id={`image-${id}`}
                      component={{
                        id: `image-${id}`,
                        type: ComponentType.Image,
                        url: imageUrl,
                        alt: `dollar-handle image ${index}`,
                        styles: {
                          height: '100%',
                          objectFit: 'cover',
                          objectPosition: styles?.[`objectPosition-${index}`],
                        },
                      }}
                    />
                  </Flex>
                ) : (
                  <Flex
                    w="100%"
                    alignSelf="stretch"
                    position="relative"
                    flex={1}
                    minW={styles?.imageWidth ? `${styles.imageWidth}px` : '100px'}
                  >
                    <Component
                      id={`image-${id}`}
                      component={{
                        id: `image-${id}`,
                        type: ComponentType.Image,
                        url: imageUrl,
                        alt: `dollar-handle image ${index}`,
                        styles: {
                          height: isHorizontal ? 'auto' : '100%',
                          objectFit: 'cover',
                          objectPosition: styles?.[`objectPosition-${index}`],
                        },
                      }}
                    />
                  </Flex>
                ))}
              <Stack
                w="100%"
                h={metrics.equalizeDescriptions ? '100%' : undefined}
                flex={metrics.equalizeDescriptions ? 1 : undefined}
                minW={metrics.equalizeDescriptions ? 0 : undefined}
                alignSelf="stretch"
                position="relative"
                gap={0}
                justifyContent={metrics.descriptionAlign ? descriptionJustify(metrics.descriptionAlign) : undefined}
                minH={descriptionMinHeight ? `${descriptionMinHeight}px` : undefined}
                {...(metrics.descriptionPadding
                  ? { padding: metrics.descriptionPadding }
                  : isHorizontal
                    ? { paddingX: '8px' }
                    : { padding: '16px' })}
              >
                {metrics.equalizeDescriptions ? (
                  <Box
                    ref={(node: HTMLElement) => setDescriptionRef(index, node)}
                    w="100%"
                    h="auto"
                    flex="0 0 auto"
                    alignSelf="flex-start"
                  >
                    {hasDescription && description && (
                      <Component
                        id={`description-${id}`}
                        component={{
                          id: `description-${id}`,
                          type: ComponentType.Text,
                          textValue: description || EMPTY_TEXT,
                        }}
                      />
                    )}
                  </Box>
                ) : (
                  hasDescription &&
                  description && (
                    <Component
                      id={`description-${id}`}
                      component={{
                        id: `description-${id}`,
                        type: ComponentType.Text,
                        textValue: description || EMPTY_TEXT,
                      }}
                    />
                  )
                )}
              </Stack>
            </Stack>
          )
        })}
      </Stack>
    </Flex>
  )
}

export default DollarHandles
